/**
 * Цель подготовки: какой экзамен сдаёт ученик. Решение владельца от 30 октября
 * 2026 (docs/02-decisions.md): цель — переменная, лицей БГУ в июне — один её
 * вариант. Ученик выбирает её на старте, до диагностики.
 *
 * Цель определяет: название в интерфейсе, дату (`learners.examDate`) и какие
 * навыки карты ей нужны. Навыки вне цели не исчезают: они идут в конце плана
 * как «сверх цели» (convex/lib/scheduleView.ts).
 *
 * Списки «вне цели» — модуль кода, а не поле слепка карты, по той же причине,
 * что темы плана (./planTopics.ts): слепок читает почти каждая функция, и в
 * сентябре 2026 проект упёрся в лимит Database I/O его байтами. Списки
 * выведены из `data/skills.json` (`exam.oge`, `exam.lyceum`, `layer`), и
 * tests/targets.test.ts держит их в согласии с картой.
 *
 * Российский порядок тем и задачи формата ОГЭ — после учебного года 2026/27,
 * после проверки на сыне (решение владельца). До тех пор у ОГЭ белорусский
 * порядок плана и общий банк задач.
 */

export type TargetId = "entrance10" | "oge" | "by9final" | "custom";

export const TARGET_IDS: readonly TargetId[] = ["entrance10", "oge", "by9final", "custom"];

export const TARGETS: Record<TargetId, { title: string; hint: string }> = {
  entrance10: {
    title: "Вступительный в лицей или гимназию",
    hint: "Экзамен в 10 класс профильного лицея или гимназии. Повышенный уровень входит.",
  },
  oge: {
    title: "ОГЭ по математике",
    hint: "Основной государственный экзамен, Россия. Порядок тем пока по белорусской программе.",
  },
  by9final: {
    title: "Выпускной экзамен 9 класса",
    hint: "Письменный экзамен по математике за базовую школу, Беларусь. Без повышенного уровня.",
  },
  custom: {
    title: "Своя цель",
    hint: "Назови её сам и поставь дату — план посчитается к ней по всей карте.",
  },
};

/** Длина названия своей цели. */
export const TARGET_NAME_LIMIT = 60;

/**
 * Навыки вне цели. Лицей — `exam.lyceum = false`; ОГЭ — `exam.oge = false`;
 * выпускной 9 класса — повышенный слой (`layer = "advanced"`): экзамен идёт по
 * школьной программе, а повышенный слой её превышает. **Последнее — допущение**,
 * сверить со сборником заданий выпускного экзамена («Академия образования»).
 */
export const OUTSIDE: Record<TargetId, readonly string[]> = {
  entrance10: [
    "num.estimate",
    "num.natural.ops",
    "num.neg.sign",
  ],
  oge: [
    "adv.abs.inequality",
    "adv.coord.parabola",
    "adv.divis.proof",
    "adv.divis.remainders",
    "adv.ineq.amgm",
    "adv.ineq.estimate",
    "adv.int.diophantine",
    "adv.int.estimate",
    "adv.param.graphic",
    "adv.param.ineq",
    "adv.param.linear",
    "adv.param.quad.disc",
    "adv.param.roots.vieta",
    "adv.pigeonhole",
    "adv.sym.cyclic",
    "alg.expr.identity",
    "alg.fsu.cube.binom",
    "eq.circle",
    "eq.irrational.simple",
    "geo.circle.angles.outside",
    "ineq.abs",
    "ineq.intervals",
    "ineq.param",
    "ineq.proof.square",
    "ineq.quad.intervals",
    "ineq.rational",
    "seq.geom.infinite",
    "word.integer.search",
  ],
  by9final: [
    "adv.abs.equation",
    "adv.abs.graph",
    "adv.abs.inequality",
    "adv.coord.parabola",
    "adv.divis.proof",
    "adv.divis.remainders",
    "adv.identity.conditional",
    "adv.ineq.amgm",
    "adv.ineq.estimate",
    "adv.int.diophantine",
    "adv.int.estimate",
    "adv.param.graphic",
    "adv.param.ineq",
    "adv.param.linear",
    "adv.param.quad.disc",
    "adv.param.roots.vieta",
    "adv.pigeonhole",
    "adv.square.complete",
    "adv.sym.cyclic",
    "eq.irrational.simple",
    "ineq.param",
    "ineq.proof.square",
    "seq.geom.infinite",
    "word.integer.search",
    "word.motion.circle",
  ],
  custom: [],
};

const OUTSIDE_SET: Record<TargetId, ReadonlySet<string>> = {
  entrance10: new Set(OUTSIDE.entrance10),
  oge: new Set(OUTSIDE.oge),
  by9final: new Set(OUTSIDE.by9final),
  custom: new Set(),
};

/** Цель ученика. Не выбрана — вступительный: так заведены ученики до 30 октября 2026. */
export function targetOf(learner: { target?: TargetId | null }): TargetId {
  return learner.target ?? "entrance10";
}

/** Название экзамена для интерфейса: «ОГЭ по математике» или своё название. */
export function examTitle(learner: { target?: TargetId | null; targetName?: string | null }): string {
  const t = targetOf(learner);
  if (t === "custom") return learner.targetName?.trim() || "Своя цель";
  return TARGETS[t].title;
}

export function inTarget(target: TargetId, skillId: string): boolean {
  return !OUTSIDE_SET[target].has(skillId);
}
